import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, RefreshCw, AlertCircle, Building2, Tag, Wallet, PiggyBank, HandCoins, Loader, CheckCircle2,
} from 'lucide-react';
import apiClient from '../services/api/client';

type ComissaoStatus = 'aguardando' | 'disponivel' | 'solicitado' | 'pago';

interface Cliente {
  id: string;
  empresa: string;
  plano: string;
  valor: number | null;                 // mensalidade regular
  primeiraMensalidade: number | null;   // 1ª mensalidade (com desconto do cupom)
  cupom: string | null;
  influencer: string | null;
  created_at: string | null;
  pago: boolean;
  comissaoPct: number;
  comissao: number;
  comissaoStatus: ComissaoStatus;
  comissaoPagaEm: string | null;
}

interface Totais {
  aReceber: number;
  acumulado: number;
  resgatado: number;
  emSolicitacao: number;
  aguardando: number;
}

const TOTAIS_VAZIO: Totais = { aReceber: 0, acumulado: 0, resgatado: 0, emSolicitacao: 0, aguardando: 0 };

const STATUS: Record<ComissaoStatus, { label: string; cls: string }> = {
  aguardando: { label: 'Aguardando pagamento do cliente', cls: 'bg-dark-700 text-dark-400 border-dark-600' },
  disponivel: { label: 'Disponível',  cls: 'bg-green-50 text-green-800 border-green-300' },
  solicitado: { label: 'Pagamento solicitado', cls: 'bg-sky-50 text-sky-800 border-sky-300' },
  pago:       { label: 'Pago',        cls: 'bg-primary-500/15 text-primary-300 border-primary-500/25' },
};

function formatBRL(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

export default function InfluencerClients() {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [totais, setTotais] = useState<Totais>(TOTAIS_VAZIO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [influencer, setInfluencer] = useState('');
  const [userName, setUserName] = useState('');

  const [marcados, setMarcados] = useState<Record<string, boolean>>({});
  const [confirmar, setConfirmar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    const storedData = localStorage.getItem('user_info');
    if (storedData) {
      try {
        const user = JSON.parse(storedData);
        setUserName(user.nome || user.username || user.login || '');
        // O identificador do influencer é o usuário do cadastro (o servidor confirma pelo login)
        const code = (user.login || user.usuario || '').toLowerCase();
        setInfluencer(code);
      } catch (e) {
        console.error('Erro ao ler user_info', e);
      }
    }
  }, []);

  useEffect(() => {
    if (influencer) {
      loadClientes(influencer);
    }
  }, [influencer]);

  const loadClientes = async (code: string) => {
    if (!code) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.get('/influencer-clientes', { params: { influencer: code } });
      setClientes(resp.data.clientes || []);
      setTotais({ ...TOTAIS_VAZIO, ...(resp.data.totais || {}) });
      setMarcados({});
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar clientes.');
    } finally {
      setLoading(false);
    }
  };

  const disponiveis = useMemo(() => clientes.filter(c => c.comissaoStatus === 'disponivel' && c.comissao > 0), [clientes]);
  const selecionados = disponiveis.filter(c => marcados[c.id]);
  const totalSelecionado = selecionados.reduce((acc, c) => acc + (c.comissao || 0), 0);
  const todosMarcados = disponiveis.length > 0 && disponiveis.every(c => marcados[c.id]);

  const solicitarPagamento = async () => {
    if (selecionados.length === 0) return;
    setEnviando(true);
    setConfirmar(false);
    setAviso(null);
    try {
      const resp = await apiClient.post('/influencer-clientes/solicitar-pagamento', {
        ids: selecionados.map(c => c.id),
      });
      setAviso({ ok: true, msg: `Pagamento solicitado: ${resp.data.quantidade} comissão(ões), total ${formatBRL(resp.data.total)}.` });
      await loadClientes(influencer);
    } catch (err: any) {
      setAviso({ ok: false, msg: err.response?.data?.error || err.message || 'Não foi possível solicitar o pagamento.' });
    } finally {
      setEnviando(false);
    }
  };

  return (
      <div className="p-6 md:p-8 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-dark-100 flex items-center gap-3">
              <Users className="w-7 h-7 text-primary-400" />
              Meus Clientes
            </h1>
            {influencer && (
                <p className="text-dark-400 text-sm mt-1">
                  Influencer: <span className="font-semibold text-primary-400">{influencer}</span>
                  {userName && <span className="ml-2 text-dark-500">· {userName}</span>}
                </p>
            )}
          </div>
          <button
              onClick={() => loadClientes(influencer)}
              disabled={loading || !influencer}
              className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 border border-dark-600 text-dark-300 hover:text-dark-100 rounded-xl transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>

        {/* Cards de resumo */}
        {!loading && !error && (
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-dark-800/60 border border-green-300 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Wallet className="w-5 h-5 text-green-700" />
                  <span className="text-dark-400 text-sm">A receber</span>
                </div>
                <p className="text-3xl font-bold text-green-800">{formatBRL(totais.aReceber)}</p>
                <p className="text-xs text-dark-500 mt-1">Disponível para solicitar</p>
              </div>

              <div className="bg-dark-800/60 border border-dark-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <PiggyBank className="w-5 h-5 text-yellow-700" />
                  <span className="text-dark-400 text-sm">Acumulado</span>
                </div>
                <p className="text-3xl font-bold text-dark-100">{formatBRL(totais.acumulado)}</p>
                <p className="text-xs text-dark-500 mt-1">
                  Total ganho{totais.aguardando > 0 && <> · + {formatBRL(totais.aguardando)} aguardando clientes pagarem</>}
                </p>
              </div>

              <div className="bg-dark-800/60 border border-dark-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-primary-400" />
                  <span className="text-dark-400 text-sm">Resgatado</span>
                </div>
                <p className="text-3xl font-bold text-dark-100">{formatBRL(totais.resgatado)}</p>
                <p className="text-xs text-dark-500 mt-1">
                  Já pago{totais.emSolicitacao > 0 && <> · {formatBRL(totais.emSolicitacao)} em solicitação</>}
                </p>
              </div>

              <div className="bg-dark-800/60 border border-dark-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="w-5 h-5 text-primary-400" />
                  <span className="text-dark-400 text-sm">Clientes indicados</span>
                </div>
                <p className="text-3xl font-bold text-dark-100">{clientes.length}</p>
                <p className="text-xs text-dark-500 mt-1">{clientes.filter(c => c.pago).length} já pagaram</p>
              </div>
            </div>
        )}

        {/* Estado de loading */}
        {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center space-y-3">
                <RefreshCw className="w-8 h-8 text-primary-400 animate-spin mx-auto" />
                <p className="text-dark-400">Carregando clientes...</p>
              </div>
            </div>
        )}

        {/* Estado de erro */}
        {!loading && error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-300 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
        )}

        {/* Sem influencer configurado */}
        {!loading && !error && !influencer && (
            <div className="text-center py-20 text-dark-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum código de influencer associado a este usuário.</p>
              <p className="text-sm mt-1">Contate o administrador.</p>
            </div>
        )}

        {/* Barra de solicitação de pagamento */}
        {!loading && !error && clientes.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-dark-800/60 border border-dark-700/50 rounded-xl">
              <div className="text-sm">
                {aviso ? (
                    <span className={aviso.ok ? 'text-green-800' : 'text-red-800'}>{aviso.msg}</span>
                ) : selecionados.length > 0 ? (
                    <span className="text-dark-200">
                      {selecionados.length} selecionado(s) · <strong className="text-green-800">{formatBRL(totalSelecionado)}</strong>
                    </span>
                ) : (
                    <span className="text-dark-400">
                      {disponiveis.length > 0
                          ? 'Selecione as comissões disponíveis para solicitar o pagamento.'
                          : 'Nenhuma comissão disponível para solicitar no momento.'}
                    </span>
                )}
              </div>
              {confirmar ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-dark-200">Solicitar {formatBRL(totalSelecionado)}?</span>
                    <button onClick={() => setConfirmar(false)}
                            className="px-3 py-2 text-sm rounded-lg border border-dark-600 text-dark-300 hover:bg-dark-700">
                      Cancelar
                    </button>
                    <button onClick={solicitarPagamento}
                            className="px-3 py-2 text-sm rounded-lg bg-green-600 hover:bg-green-500 text-white">
                      Confirmar
                    </button>
                  </div>
              ) : (
                  <button
                      onClick={() => { setAviso(null); setConfirmar(true); }}
                      disabled={enviando || selecionados.length === 0}
                      title="Solicitar pagamento das comissões selecionadas"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {enviando ? <Loader className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />}
                    Solicitar pagamento
                  </button>
              )}
            </div>
        )}

        {/* Tabela */}
        {!loading && !error && clientes.length > 0 && (
            <div className="bg-dark-800/60 border border-dark-700/50 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                  <tr className="border-b border-dark-700/50">
                    <th className="px-4 py-4 w-10">
                      <input type="checkbox" checked={todosMarcados} disabled={disponiveis.length === 0}
                             title="Selecionar todas as comissões disponíveis"
                             onChange={e => setMarcados(prev => {
                               const n = { ...prev };
                               disponiveis.forEach(c => { n[c.id] = e.target.checked; });
                               return n;
                             })} />
                    </th>
                    <th className="px-5 py-4 text-left text-dark-400 font-medium">Empresa</th>
                    <th className="px-5 py-4 text-left text-dark-400 font-medium">Plano</th>
                    <th className="px-5 py-4 text-left text-dark-400 font-medium">Cupom</th>
                    <th className="px-5 py-4 text-right text-dark-400 font-medium">1ª mensalidade</th>
                    <th className="px-5 py-4 text-right text-dark-400 font-medium">Você recebe</th>
                    <th className="px-5 py-4 text-left text-dark-400 font-medium">Status</th>
                    <th className="px-5 py-4 text-left text-dark-400 font-medium">Cadastro</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-700/30">
                  {clientes.map((c) => {
                    const podeSelecionar = c.comissaoStatus === 'disponivel' && c.comissao > 0;
                    const st = STATUS[c.comissaoStatus] || STATUS.aguardando;
                    return (
                        <tr key={c.id} className="hover:bg-dark-700/30 transition-colors">
                          <td className="px-4 py-4">
                            <input type="checkbox" disabled={!podeSelecionar} checked={!!marcados[c.id]}
                                   title={podeSelecionar ? 'Selecionar para solicitar pagamento' : st.label}
                                   onChange={e => { setAviso(null); setMarcados(prev => ({ ...prev, [c.id]: e.target.checked })); }} />
                          </td>
                          <td className="px-5 py-4 text-dark-100 font-medium">{c.empresa || '—'}</td>
                          <td className="px-5 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                (c.plano || '').toLowerCase().includes('platinum')
                                    ? 'bg-slate-300/15 text-slate-200 border border-slate-300/30'
                                    : (c.plano || '').toLowerCase().includes('gold')
                                    ? 'bg-yellow-50 text-yellow-800 border border-yellow-300'
                                    : 'bg-primary-500/15 text-primary-300 border border-primary-500/20'
                            }`}>
                              {c.plano || '—'}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            {c.cupom ? (
                                <span className="inline-flex items-center gap-1 text-dark-300">
                                  <Tag className="w-3 h-3" />
                                  {c.cupom}
                                </span>
                            ) : <span className="text-dark-600">—</span>}
                          </td>
                          <td className="px-5 py-4 text-right text-dark-200">{formatBRL(c.primeiraMensalidade)}</td>
                          <td className="px-5 py-4 text-right">
                            <span className="text-green-800 font-semibold">{formatBRL(c.comissao)}</span>
                            {c.comissaoPct > 0 && <p className="text-[11px] text-dark-500">{c.comissaoPct}% da 1ª mensalidade</p>}
                          </td>
                          <td className="px-5 py-4">
                            <span className={`inline-block px-2 py-0.5 rounded border text-xs whitespace-nowrap ${st.cls}`}>
                              {st.label}
                            </span>
                            {c.comissaoStatus === 'pago' && c.comissaoPagaEm && (
                                <p className="text-[11px] text-dark-500 mt-0.5">em {formatDate(c.comissaoPagaEm)}</p>
                            )}
                          </td>
                          <td className="px-5 py-4 text-dark-400">{formatDate(c.created_at)}</td>
                        </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
            </div>
        )}

        {/* Lista vazia */}
        {!loading && !error && influencer && clientes.length === 0 && (
            <div className="text-center py-20 text-dark-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum cliente encontrado para <strong className="text-dark-400">{influencer}</strong>.</p>
            </div>
        )}
      </div>
  );
}
